import PropTypes from 'prop-types'
import { Box, Divider, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import { getCurrencySymbol as _getCurrencySymbol } from '@hapana/shared/utils/string.util'

const getCurrencySymbol = (currency) => (currency ? _getCurrencySymbol(currency) : '$')
import Button from '../../button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '../../../../components/shared/button/button.constants'
import Accordion from '../../../../components/shared/accordion/accordion.component'
import { formatDistanceToNowAbbreviated as _formatDistanceToNowAbbreviated } from '@hapana/shared/utils/date.util'

const formatDistanceToNowAbbreviated = (date) => {
  if (!date || isNaN(date.getTime())) {
    return ''
  }
  return _formatDistanceToNowAbbreviated(date)
}
import { isLastElement } from '@hapana/shared/utils/array.util'

import { RECURRING_PAYMENT } from '../individual-alerts.constants'

import AlertSection from '../alert-section/alert-section.component'
import SinglePayment from './single-payment.component'
import { styles } from './payments-alert.styles'

const PaymentsAlert = ({
  failedPayments,
  siteId,
  isHomeLocation,
  destinationFee,
  onPayFee,
  isStaging,
}) => {
  const { t } = useTranslation(['checkIn'])
  const hasFailed = failedPayments?.length > 0
  const hasMultiple = failedPayments?.length > 1
  const count = failedPayments?.length || 0
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)

  const getRetryUrl = (invoiceId) =>
    `/s/${siteId}/retail/checkout?mode=retry&invoiceId=${invoiceId}`

  const failedPaymentsUrl = `/s/${siteId}/retail/failed`

  const paymentLabel = (payment) => {
    if (payment.label) {
      return payment.label
    } else if (payment?.v1PaymentType?.toUpperCase() === RECURRING_PAYMENT) {
      return t(`checkIn:recurringPayment`)
    }
    return t('checkIn:payments')
  }

  return (
    <AlertSection
      type="payment"
      title={hasFailed ? t('checkIn:failedPayment') : t('checkIn:payments')}
      count={count > 1 ? t('checkIn:totalAlertsCard', { count }) : undefined}
      viewAllLabel={count > 1 ? t('checkIn:viewAllPayments') : undefined}
      viewAllHref={count > 1 ? failedPaymentsUrl : undefined}
    >
      {hasFailed &&
        (!hasMultiple ? (
          <SinglePayment
            payment={failedPayments[0]}
            label={paymentLabel(failedPayments[0])}
            href={isHomeLocation ? getRetryUrl(failedPayments[0].invoiceId) : undefined}
            isOpen={isAccordionOpen}
            onChange={setIsAccordionOpen}
            canRetry={isHomeLocation}
          />
        ) : (
          <Accordion
            containerStyles={styles.noPadding}
            summaryStyles={{
              ...styles.accordionSummary,
              ...(isAccordionOpen && styles.openedSummary),
            }}
            contentStyles={styles.multipleAccordionContent}
            onChange={setIsAccordionOpen}
            isOpen={isAccordionOpen}
            content={failedPayments?.slice(1).map((payment, index) => {
              const retryUrl = getRetryUrl(payment.invoiceId)
              const firstRetryUrl = getRetryUrl(failedPayments[0].invoiceId)
              return (
                <Box
                  key={payment.id}
                  sx={{
                    ...styles.multiplePaymentWrapper,
                    ...(isAccordionOpen && index === 0 && styles.openedMultiplePaymentWrapper),
                  }}
                >
                  {index === 0 && isHomeLocation && (
                    <Box sx={styles.firstButtonWrapper}>
                      <Button
                        variant={BUTTON_VARIANTS.TEXT}
                        size={BUTTON_SIZE.SMALL}
                        theme={BUTTON_THEME.PRIMARY}
                        stroke
                        label={t('checkIn:retryPayment')}
                        href={firstRetryUrl}
                      />
                      <Divider
                        sx={{
                          ...styles.internalDivider,
                          ...styles.internalDividerWithOneType,
                        }}
                      />
                    </Box>
                  )}
                  {index === 0 && !isHomeLocation && <Divider sx={styles.internalDivider} />}
                  <Box sx={styles.flexRow}>
                    <Typography typography="header4" color="gray.64">
                      {paymentLabel(payment)}
                    </Typography>
                    {payment.datetime && (
                      <>
                        <Typography typography="header4" color="gray.56" sx={styles.spacer}>
                          •
                        </Typography>
                        <Typography typography="header4" color="gray.56">
                          {formatDistanceToNowAbbreviated(new Date(payment.datetime))}
                        </Typography>
                      </>
                    )}
                  </Box>
                  {(payment.price != null || payment.siteName) && (
                    <Box sx={styles.flexRow}>
                      <Typography typography="medium" color="gray.64">
                        {[
                          payment.price != null
                            ? `${getCurrencySymbol(payment.currency)} ${payment.price}`
                            : null,
                          payment.siteName,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      </Typography>
                    </Box>
                  )}
                  {isHomeLocation && (
                    <Box sx={styles.buttonWrapper}>
                      <Button
                        variant={BUTTON_VARIANTS.TEXT}
                        size={BUTTON_SIZE.SMALL}
                        theme={BUTTON_THEME.PRIMARY}
                        stroke
                        label={t('checkIn:retryPayment')}
                        href={retryUrl}
                      />
                    </Box>
                  )}
                  <Box>
                    {isLastElement(failedPayments.slice(1), index) ? (
                      <Divider sx={styles.internalDivider} />
                    ) : null}
                  </Box>
                </Box>
              )
            })}
            groupedRows
            label={
              <>
                {isAccordionOpen && (
                  <>
                    <Box sx={styles.flexRow}>
                      <Typography typography="header4" color="gray.64">
                        {paymentLabel(failedPayments[0])}
                      </Typography>
                      {failedPayments[0].datetime && (
                        <>
                          <Typography typography="header4" color="gray.56" sx={styles.spacer}>
                            •
                          </Typography>
                          <Typography typography="header4" color="gray.56">
                            {formatDistanceToNowAbbreviated(new Date(failedPayments[0].datetime))}
                          </Typography>
                        </>
                      )}
                    </Box>
                    {(failedPayments[0].price != null || failedPayments[0].siteName) && (
                      <Box sx={styles.flexRow}>
                        <Typography typography="medium" color="gray.64">
                          {[
                            failedPayments[0].price != null
                              ? `${getCurrencySymbol(failedPayments[0].currency)} ${failedPayments[0].price}`
                              : null,
                            failedPayments[0].siteName,
                          ]
                            .filter(Boolean)
                            .join(' • ')}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
                {!isAccordionOpen && (
                  <Box sx={styles.flexRow}>
                    <Typography typography="medium" color="gray.64">
                      {failedPayments.map((payment) => paymentLabel(payment)).join(', ')}
                    </Typography>
                  </Box>
                )}
              </>
            }
          />
        ))}
      {destinationFee && (
        <Box>
          {hasFailed && <Divider sx={styles.internalDivider} />}
          <Box sx={styles.flexRow}>
            <Typography typography="header4" color="gray.64">
              {t('checkIn:holidayDestinationFee')}
            </Typography>
          </Box>
          <Box sx={styles.flexRow}>
            <Typography typography="medium" color="gray.64">
              {[
                destinationFee.amount != null
                  ? `${getCurrencySymbol(destinationFee.currency)} ${destinationFee.amount}`
                  : null,
                destinationFee.membershipName,
                destinationFee.siteName,
              ]
                .filter(Boolean)
                .join(' • ')}
            </Typography>
          </Box>
          <Box sx={styles.buttonWrapper}>
            <Button
              variant={BUTTON_VARIANTS.TEXT}
              size={BUTTON_SIZE.SMALL}
              theme={BUTTON_THEME.PRIMARY}
              stroke
              label={t('checkIn:payFee')}
              onClick={onPayFee}
              isLoading={isStaging}
              disabled={isStaging}
            />
          </Box>
        </Box>
      )}
    </AlertSection>
  )
}

PaymentsAlert.propTypes = {
  failedPayments: PropTypes.array,
  siteId: PropTypes.string,
  isHomeLocation: PropTypes.bool,
  destinationFee: PropTypes.shape({
    amount: PropTypes.string,
    currency: PropTypes.string,
    siteName: PropTypes.string,
    membershipName: PropTypes.string,
    existingStagedInvoiceId: PropTypes.string,
  }),
  onPayFee: PropTypes.func,
  isStaging: PropTypes.bool,
}

export default PaymentsAlert
